import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowUp, X, RotateCcw, CheckCircle2, AlertTriangle, ChevronUp, Minimize2, FolderOpen, Clock, Check,
} from 'lucide-react'
import {
  formatBytesFr, formatClock, formatElapsed, formatRemaining, folderLabel,
  percentOf, sortForDisplay, summarizeQueue,
} from './mediaLibraryFormat.js'
import { MediaExtBadge } from './MediaExtBadge.js'
import type { UploadTask } from './useUploadQueue.js'
import './UploadCenter.css'

/** Durée d'affichage de la notice « Ajouté à … » après un envoi réussi. */
const NOTICE_MS = 6000

interface UploadCenterProps {
  tasks:           UploadTask[]
  onCancel:        (id: string) => void
  onCancelAll:     () => void
  onRetry:         (id: string) => void
  onDismiss:       (id: string) => void
  onClearFinished: () => void
  /** Ouvre le dossier d'un fichier envoyé et le met en évidence. */
  onReveal?:       (task: UploadTask) => void
  /** État initial réduit (stories et captures). */
  defaultCollapsed?: boolean
}

/**
 * Suivi flottant des envois, ancré en bas à droite et au-dessus des fenêtres :
 * déplié, il détaille la file ; réduit, il tient sur une ligne avec un anneau
 * de progression. La fin d'un envoi le déplie et annonce le dossier d'arrivée.
 */
export const UploadCenter = memo(function UploadCenter({
  tasks, onCancel, onCancelAll, onRetry, onDismiss, onClearFinished, onReveal, defaultCollapsed = false,
}: UploadCenterProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const [notice, setNotice] = useState<UploadTask | null>(null)
  // Les envois déjà terminés au montage ne déclenchent ni notice ni dépliage.
  const seenDone = useRef(new Set(tasks.filter(t => t.status === 'done').map(t => t.id)))

  // Un envoi qui aboutit déplie le suivi et affiche la notice d'arrivée.
  useEffect(() => {
    const fresh = tasks.filter(t => t.status === 'done' && !seenDone.current.has(t.id))
    if (fresh.length === 0) return
    for (const t of fresh) seenDone.current.add(t.id)
    const latest = fresh[fresh.length - 1]
    setNotice(latest)
    setCollapsed(false)
    const timer = window.setTimeout(() => setNotice(n => (n?.id === latest.id ? null : n)), NOTICE_MS)
    return () => window.clearTimeout(timer)
  }, [tasks])

  const summary = useMemo(() => summarizeQueue(tasks), [tasks])
  const ordered = useMemo(() => sortForDisplay(tasks), [tasks])
  const pendingIds = useMemo(() => ordered.filter(t => t.status === 'pending').map(t => t.id), [ordered])
  const current = tasks.find(t => t.status === 'uploading') ?? null

  const expand   = useCallback(() => setCollapsed(false), [])
  const collapse = useCallback(() => setCollapsed(true), [])
  const closeAll = useCallback(() => { for (const t of tasks) onDismiss(t.id) }, [tasks, onDismiss])

  if (tasks.length === 0) return null

  const busy = summary.active > 0
  const remaining = formatRemaining(summary.remainingSec)
  const subtitle = summary.position != null
    ? [`${summary.position} sur ${summary.total}`, remaining].filter(Boolean).join(' · ')
    : `${summary.total} envoi${summary.total > 1 ? 's' : ''} traité${summary.total > 1 ? 's' : ''}`

  if (collapsed) {
    const pct = summary.currentPercent ?? 100
    const title = current
      ? [`Envoi ${summary.position} sur ${summary.total}`, formatRemaining(current.etaSec)].filter(Boolean).join(' · ')
      : busy ? 'Envois en attente' : 'Envois terminés'
    return (
      <section className="upload-center upload-center--mini" aria-label="Suivi des envois">
        <button type="button" className="upload-center__mini" onClick={expand} aria-expanded={false} aria-label={`${title}. Déplier le suivi`}>
          <ProgressRing percent={pct} active={!!current} />
          <span className="upload-center__mini-text">
            <span className="upload-center__mini-title">{title}</span>
            <span className="upload-center__mini-file">{current?.name ?? ordered[0]?.name}</span>
          </span>
          {summary.failed > 0 && (
            <span className="upload-center__mini-fail" title={`${summary.failed} échec${summary.failed > 1 ? 's' : ''}`}>
              <AlertTriangle size={13} aria-hidden /> {summary.failed}
            </span>
          )}
          <ChevronUp size={15} aria-hidden className="upload-center__mini-chevron" />
        </button>
        <span className="upload-center__mini-track" aria-hidden>
          <span className={`upload-center__mini-fill${current ? ' is-active' : ''}`} style={{ width: `${pct}%` }} />
        </span>
      </section>
    )
  }

  return (
    <section className="upload-center" aria-label="Suivi des envois">
      <header className="upload-center__head">
        <span className={`upload-center__badge${busy ? ' is-busy' : ''}`} aria-hidden>
          <ArrowUp size={15} />
        </span>
        <div className="upload-center__heading">
          <span className="upload-center__title">Envois vers le serveur</span>
          <span className="upload-center__sub" aria-live="polite">{subtitle}</span>
        </div>
        <button type="button" className="upload-center__icon" onClick={collapse} aria-label="Réduire le suivi" title="Réduire">
          <Minimize2 size={14} aria-hidden />
        </button>
        {!busy && (
          <button type="button" className="upload-center__icon" onClick={closeAll} aria-label="Fermer le suivi" title="Fermer">
            <X size={15} aria-hidden />
          </button>
        )}
      </header>

      <div className="upload-center__queue" aria-hidden>
        {tasks.map(t => (
          <span key={t.id} className="upload-center__segment">
            <span
              className={`upload-center__segment-fill upload-center__segment-fill--${t.status}`}
              style={{ width: `${t.status === 'done' ? 100 : t.status === 'pending' ? 0 : percentOf(t)}%` }}
            />
          </span>
        ))}
      </div>

      {notice && (
        <div className="upload-center__notice" role="status">
          <CheckCircle2 size={14} aria-hidden />
          <span className="upload-center__notice-text">
            {notice.kind === 'replace'
              ? `Remplacé dans ${folderLabel(notice.destPath)}`
              : `Ajouté à ${folderLabel(notice.destPath)}`}
          </span>
          {onReveal && (
            <button type="button" className="upload-center__notice-action" onClick={() => onReveal(notice)}>Voir</button>
          )}
        </div>
      )}

      <ul className="upload-center__list">
        {ordered.map(task => (
          <UploadRow
            key={task.id}
            task={task}
            queueRank={task.status === 'pending' ? pendingIds.indexOf(task.id) + (current ? 2 : 1) : null}
            onCancel={onCancel}
            onRetry={onRetry}
            onDismiss={onDismiss}
            onReveal={onReveal}
          />
        ))}
      </ul>

      {(summary.finished > 0 || busy) && (
        <footer className="upload-center__foot">
          {summary.finished > 0
            ? <button type="button" className="upload-center__link" onClick={onClearFinished}>Effacer les terminés</button>
            : <span />}
          {busy && <button type="button" className="upload-center__link upload-center__link--quiet" onClick={onCancelAll}>Tout annuler</button>}
        </footer>
      )}
    </section>
  )
})

/** Anneau de progression du suivi réduit. */
const ProgressRing = memo(function ProgressRing({ percent, active }: { percent: number; active: boolean }) {
  const r = 11
  const c = 2 * Math.PI * r
  const clamped = Math.min(100, Math.max(0, percent))
  return (
    <span className={`upload-center__ring${active ? ' is-active' : ''}`} aria-hidden>
      <svg viewBox="0 0 26 26" width="26" height="26">
        <circle className="upload-center__ring-track" cx="13" cy="13" r={r} />
        <circle
          className="upload-center__ring-value"
          cx="13" cy="13" r={r}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - clamped / 100)}
        />
      </svg>
      <span className="upload-center__ring-label">{active ? clamped : <Check size={11} />}</span>
    </span>
  )
})

const UploadRow = memo(function UploadRow({
  task, queueRank, onCancel, onRetry, onDismiss, onReveal,
}: {
  task: UploadTask
  queueRank: number | null
  onCancel: (id: string) => void
  onRetry: (id: string) => void
  onDismiss: (id: string) => void
  onReveal?: (task: UploadTask) => void
}) {
  const pct = percentOf(task)
  const remaining = formatRemaining(task.etaSec)
  const elapsed = task.finishedAt != null && task.startedAt != null ? formatElapsed(task.finishedAt - task.startedAt) : null
  const durationChanged = task.kind === 'replace' && task.status === 'done'
    && task.previousDurationSec != null && task.durationSec != null
    && Math.abs(task.previousDurationSec - task.durationSec) >= 1

  return (
    <li className={`upload-center__row upload-center__row--${task.status}`}>
      <div className="upload-center__line">
        <MediaExtBadge name={task.name} size="sm" />
        <span className="upload-center__name" title={task.name}>
          {task.kind === 'replace' && <span className="upload-center__kind">Remplacement · </span>}
          {task.name}
        </span>

        {task.status === 'uploading' && <span className="upload-center__pct">{pct} %</span>}
        {task.status === 'pending' && (
          <span className="upload-center__pill"><Clock size={11} aria-hidden /> En file</span>
        )}
        {task.status === 'done' && (
          <>
            <span className="upload-center__pill upload-center__pill--done"><Check size={11} aria-hidden /> Transféré</span>
            {onReveal && (
              <button type="button" className="upload-center__icon" onClick={() => onReveal(task)} aria-label={`Ouvrir le dossier de ${task.name}`} title="Ouvrir le dossier">
                <FolderOpen size={14} aria-hidden />
              </button>
            )}
          </>
        )}
        {(task.status === 'error' || task.status === 'canceled') && (
          <button type="button" className="upload-center__retry" onClick={() => onRetry(task.id)}>
            <RotateCcw size={12} aria-hidden /> {task.status === 'error' ? 'Réessayer' : 'Reprendre'}
          </button>
        )}

        {task.status === 'uploading' || task.status === 'pending'
          ? (
            <button type="button" className="upload-center__icon" onClick={() => onCancel(task.id)} aria-label={`Annuler l’envoi de ${task.name}`} title="Annuler">
              <X size={14} aria-hidden />
            </button>
          )
          : task.status !== 'done' && (
            <button type="button" className="upload-center__icon" onClick={() => onDismiss(task.id)} aria-label={`Retirer ${task.name} de la liste`} title="Retirer">
              <X size={14} aria-hidden />
            </button>
          )}
      </div>

      {(task.status === 'uploading' || task.status === 'error') && (
        <div
          className="upload-center__track"
          role="progressbar"
          aria-label={`Progression de ${task.name}`}
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span className="upload-center__fill" style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="upload-center__meta">
        {task.status === 'uploading' && (
          <>
            <span>{formatBytesFr(task.loaded, 2)} / {formatBytesFr(task.size)}</span>
            {task.speed > 0 && <><span aria-hidden>·</span><span>{formatBytesFr(task.speed, 0)}/s</span></>}
            {remaining && <><span aria-hidden>·</span><span>{remaining}</span></>}
          </>
        )}
        {task.status === 'pending' && (
          <>
            <span>{formatBytesFr(task.size)}</span>
            {queueRank != null && <><span aria-hidden>·</span><span>{queueRank}ᵉ de la file</span></>}
          </>
        )}
        {task.status === 'done' && (
          <>
            <span>{formatBytesFr(task.size)}</span>
            {elapsed && <><span aria-hidden>·</span><span>{elapsed}</span></>}
            <span aria-hidden>·</span>
            <span className={task.checkOk ? undefined : 'upload-center__meta-warn'}>
              {task.checkOk ? 'contrôle média OK' : 'contrôle média à revoir'}
            </span>
          </>
        )}
        {task.status === 'error' && (
          <span className="upload-center__meta-error" title={task.error}>
            <AlertTriangle size={11} aria-hidden /> {task.error ?? 'Erreur'} · {formatBytesFr(task.loaded)} / {formatBytesFr(task.size)}
          </span>
        )}
        {task.status === 'canceled' && <span>Annulé · {formatBytesFr(task.size)}</span>}
        <span className="upload-center__target">→ {folderLabel(task.destPath)}</span>
      </div>

      {durationChanged && (
        <p className="upload-center__warn" role="note">
          <AlertTriangle size={12} aria-hidden />
          Durée {formatClock(task.previousDurationSec)} → {formatClock(task.durationSec)} : revérifiez le média dans la fiche programme.
        </p>
      )}
    </li>
  )
})
